import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Button, TextCaption2, IconInfo } from '@blocket/blocket-ui'
import {
  trackingExtensionEntranceView,
  trackingExtensionEntranceClick,
  trackTransactionInfoView,
  trackTransactionInfoClick,
} from 'tracking/chat'
import { awaitPulseTracking } from 'utils/pulse'
import getExtensionButtonType from 'components/pagesContent/Chat/utils/getChatButtonType'
import Overlay from 'components/pagesContent/Chat/components/shared/Overlay'

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.1);
  ${({ showStatusBar, isExtension }) =>
    !showStatusBar &&
    isExtension &&
    css`
      display: flex;
      padding: 8px 16px;
    `}
`

const ActiveExtensionsWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px 8px 16px;
  border-radius: 4px;
  background-color: #f0f5fb;
`

const ExtensionMessage = styled(TextCaption2)`
  ${({ addMargin }) => addMargin && 'margin-right: 16px;'}
`

const TransactionButtons = styled.div`
  max-width: fit-content;
  margin: 0 16px;
  display: flex;
  padding: ${({ adIsActive }) => (adIsActive ? '0 0 8px' : '8px 0')};

  div:first-of-type {
    ${({ singleButton }) => !singleButton && 'margin-right: 8px;'}
    flex-shrink: 1;
    flex-grow: 0;
  }

  div:last-of-type {
    flex-shrink: 0;
    flex-grow: 1;
    flex-basis: 0px;
    min-width: 66px;
  }
`

const TransactionButton = styled(Button)`
  min-width: 0;
  & button {
    ${({ isMobile, singleButton }) =>
      isMobile && !singleButton ? 'padding: 0 8px;' : 'padding: 0 24px;'}
    overflow: hidden;
    & span {
      display: inline-block;
      justify-content: flex-start;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }
`

const TransactionStatus = styled.div`
  padding: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  background-color: #d9e6f3;
`

const ExtensionEntrance = ({
  extensionData,
  selectedChannel,
  isMobile,
  adIsActive,
  overlayActive,
}) => {
  const [trackingViewSent, setTrackingViewSent] = useState(false)
  const [transactionState, setTransactionState] = useState(undefined)
  const [trackingId, setTrackingId] = useState()
  const currentUserId = useSelector((state) => state.chat.sendbirdUser.userId)

  const usersOwnAd = JSON.parse(selectedChannel.data).ad.sb_user_id === currentUserId
  const role = usersOwnAd ? 'seller' : 'buyer'
  /* The status bar should be visible in both BB and BP, if you get active_extensions info in the response */
  const showStatusBar = extensionData?.activeExtensions?.length > 0
  const isExtension = Boolean(
    extensionData?.activeExtensions?.length || extensionData?.extensions?.length,
  )
  const isTransaction = Boolean(extensionData?.transaction)

  useEffect(() => {
    if (isTransaction) {
      const currentTrackingId = extensionData.transaction?.trackingId

      if (currentTrackingId !== trackingId) {
        trackTransactionInfoView({
          selectedChannel,
          handlerId: extensionData.transaction?.handlerId,
          trackingId: extensionData.transaction?.trackingId,
          role,
        })
        setTrackingId(currentTrackingId)
      }
    }
  }, [extensionData.transaction, isTransaction, role, selectedChannel, trackingId])

  useEffect(() => {
    // we want to send in a new view event for extension Entrance if the state of it has been updated.
    if (isExtension) {
      const stateUpdateSinceTrackingSent =
        extensionData.activeExtensions[0]?.state !== transactionState
      if (!trackingViewSent || stateUpdateSinceTrackingSent) {
        trackingExtensionEntranceView(extensionData, selectedChannel, role, showStatusBar)
        setTrackingViewSent(true)
        setTransactionState(extensionData.activeExtensions[0]?.state)
      }
    }
  }, [
    extensionData,
    selectedChannel,
    role,
    showStatusBar,
    trackingViewSent,
    transactionState,
    isExtension,
  ])

  const onExtensionClick = (extension, redirectUrl) => {
    awaitPulseTracking(
      () => trackingExtensionEntranceClick(extension, selectedChannel, role),
      () => (window.location = `${redirectUrl}&mc_callback_url=${window.location.href}`),
    )
  }

  const extensionButton = extensionData?.extensions?.map((extension) => {
    return (
      <Fragment key={extension.id}>
        {extension.actionLabel && (
          <div style={{ marginRight: 8 }}>
            <Button
              type={getExtensionButtonType('primary')}
              size="tiny"
              onClick={() => onExtensionClick(extension, extension.startUrl)}
              tabIndex="0"
            >
              {extension.actionLabel}
            </Button>
          </div>
        )}
      </Fragment>
    )
  })

  const extensionStatusActionBar = extensionData?.activeExtensions?.map((extension) => {
    return (
      <ActiveExtensionsWrapper key={extension.id}>
        <ExtensionMessage addMargin={extension?.action}>{extension.message}</ExtensionMessage>
        {extension.action && (
          <Button
            type={getExtensionButtonType(extension.action.type)}
            style={{ fontSize: '12px' }}
            size="tiny"
            onClick={() => onExtensionClick(extension, extension.action.url)}
            tabIndex="0"
          >
            {extension.action.label}
          </Button>
        )}
      </ActiveExtensionsWrapper>
    )
  })

  const Extension = () => {
    const layout = showStatusBar ? extensionStatusActionBar : extensionButton
    return (
      <Wrapper data-testid="extension" showStatusBar={showStatusBar} isExtension={isExtension}>
        <Overlay show={overlayActive} />
        {layout}
      </Wrapper>
    )
  }

  const Transaction = () => {
    const transactionActions = extensionData.transaction?.actions
    const transactionStatus = extensionData.transaction?.status
    const singleButton = transactionActions?.length === 1

    if (!transactionActions && !transactionStatus) {
      return null
    }

    return (
      <Wrapper data-testid="transaction">
        <Overlay show={overlayActive} />
        {transactionActions && (
          <TransactionButtons singleButton={singleButton} adIsActive={adIsActive}>
            {transactionActions.map((action, i) => (
              <TransactionButton
                key={action.label + i}
                type={getExtensionButtonType(action.type)}
                onClick={() => {
                  awaitPulseTracking(
                    () =>
                      trackTransactionInfoClick({
                        selectedChannel,
                        handlerId: extensionData.transaction?.handlerId,
                        trackingId: action?.trackingId,
                        role,
                      }),
                    () => (window.location = action.url),
                  )
                }}
                size="tiny"
                isMobile={isMobile}
                singleButton={singleButton}
              >
                {action.label}
              </TransactionButton>
            ))}
          </TransactionButtons>
        )}
        {transactionStatus && (
          <TransactionStatus>
            <IconInfo style={{ marginRight: 8, minWidth: 16 }} width={16} height={16} />
            <TextCaption2>{transactionStatus}</TextCaption2>
          </TransactionStatus>
        )}
      </Wrapper>
    )
  }

  if (isTransaction) {
    return <Transaction />
  }

  if (isExtension) {
    return <Extension />
  }

  return null
}

ExtensionEntrance.propTypes = {
  extensionData: PropTypes.object,
  selectedChannel: PropTypes.object,
}

export default React.memo(ExtensionEntrance)
